// Cadence, projection, and service-boundary math for recurring invoice plans.
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { SERVICE_DIRECTION } from '../../contacts/enums';
import { DefaultDateFormat } from '../dates';

const BILLING_INTERVAL = {
  daily: moment.duration(1, 'day'),
  weekly: moment.duration(1, 'week'),
  biweekly: moment.duration(2, 'weeks'),
  'bi-weekly': moment.duration(2, 'weeks'),
  monthly: moment.duration(1, 'month'),
  quarterly: moment.duration(3, 'months'),
  yearly: moment.duration(1, 'year'),
};

// "Twice per month" is true semi-monthly billing: exactly two charges per calendar
// month (24 per year), anchored to month boundaries. Unlike bi-weekly (a fixed +14
// days that drifts to 26 charges/year), it cannot be expressed as a fixed
// moment.duration, so it is handled separately in the next-due-date computation below.
export const TWICE_PER_MONTH = 'twice per month';

const CANONICAL_BILLING_INTERVALS: Record<string, string> = {
  weekly: 'Weekly',
  'bi-weekly': 'Bi-Weekly',
  [TWICE_PER_MONTH]: 'Twice Per Month',
  monthly: 'Monthly',
};

export function normalizeBillingInterval(interval?: string | null) {
  if (!interval) return interval;
  return CANONICAL_BILLING_INTERVALS[interval.toLowerCase()] ?? interval;
}

export function billingIntervalsEqual(left?: string | null, right?: string | null) {
  if (typeof left !== 'string' || typeof right !== 'string') return left === right;
  return left.toLowerCase() === right.toLowerCase();
}

export function normalizeInvoiceItemIds<T extends { id?: string; discounts?: Array<{ id?: string }> }>(
  items: T[]
): T[] {
  return items.map((item) => ({
    ...item,
    id: item.id || uuidv4(),
    discounts: item.discounts?.map((discount) => ({
      ...discount,
      id: discount.id || uuidv4(),
    })),
  }));
}

function isTwicePerMonth(interval?: string) {
  return !!interval && interval.toLowerCase() === TWICE_PER_MONTH;
}

// NOTE: getSemiMonthlyAnchors / getNextTwicePerMonthDue / getPreviousTwicePerMonthDue
// below are kept byte-for-byte in sync with functions/src/helpers/invoicePlans.js (no
// shared package exists between frontend and functions). Update both.

/**
 * Compute the two semi-monthly ("twice per month") charge days for a given month,
 * derived from the billing anchor day `D` (the day-of-month of the plan's first due date).
 *
 * The two charge days are always ~15 days apart and both fall inside the month:
 * - `D` in the first half (`<= 15`): charge on `D` and `D + 15`.
 * - `D` in the second half (`>= 16`): charge on `D - 15` and `D`.
 *
 * Only the later (end-of-month) anchor is ever clamped to the last day of the month
 * (e.g. the 30th -> Feb 28, or Feb 29 in a leap year); the earlier anchor is always
 * `<= 16`, so it never clamps. This yields exactly two distinct, evenly-spaced charges
 * every calendar month (24 per year) for any start day, with no month-end bunching.
 */
export function getSemiMonthlyAnchors(year: number, monthIndex: number, anchorDay: number) {
  const lastDay = moment({ year, month: monthIndex }).daysInMonth();
  if (anchorDay <= 15) {
    return { firstAnchor: anchorDay, secondAnchor: Math.min(anchorDay + 15, lastDay) };
  }
  return { firstAnchor: anchorDay - 15, secondAnchor: Math.min(anchorDay, lastDay) };
}

/**
 * Given the current due date and the billing anchor day `D`, return the next
 * semi-monthly ("twice per month") due date. This advances within the same month
 * from the first anchor to the second, then rolls into the first anchor of the
 * following month, yielding exactly two due dates per calendar month (24 per year).
 */
export function getNextTwicePerMonthDue(momentDue, anchorDay: number) {
  const year = momentDue.year();
  const monthIndex = momentDue.month();
  const day = momentDue.date();
  const { secondAnchor } = getSemiMonthlyAnchors(year, monthIndex, anchorDay);

  if (day < secondAnchor) {
    return momentDue.clone().date(secondAnchor);
  }

  const nextMonth = momentDue.clone().add(1, 'month').startOf('month');
  const { firstAnchor } = getSemiMonthlyAnchors(nextMonth.year(), nextMonth.month(), anchorDay);
  return nextMonth.date(firstAnchor);
}

/**
 * Inverse of getNextTwicePerMonthDue (the inverse only holds when both calls receive
 * the same `anchorDay`): given the current due date and the billing anchor day `D`,
 * return the previous semi-monthly ("twice per month") due date. Used to derive the
 * service period covered by a charge.
 */
export function getPreviousTwicePerMonthDue(momentDue, anchorDay: number) {
  const year = momentDue.year();
  const monthIndex = momentDue.month();
  const day = momentDue.date();
  const { firstAnchor } = getSemiMonthlyAnchors(year, monthIndex, anchorDay);

  if (day > firstAnchor) {
    return momentDue.clone().date(firstAnchor);
  }

  const previousMonth = momentDue.clone().subtract(1, 'month').startOf('month');
  const { secondAnchor } = getSemiMonthlyAnchors(previousMonth.year(), previousMonth.month(), anchorDay);
  return previousMonth.date(secondAnchor);
}

export function getBillingDuration(interval) {
  if (!interval) return null;
  return BILLING_INTERVAL[interval.toLowerCase()];
}

// Charges per year for each fixed-cadence interval. Bi-weekly is 26 (every 14 days),
// twice-per-month is 24 (two per calendar month) — the distinction behind CCMS-7871's
// over-billing fix.
export const ANNUAL_CHARGE_COUNTS: Record<string, number> = {
  daily: 365,
  weekly: 52,
  biweekly: 26,
  'bi-weekly': 26,
  [TWICE_PER_MONTH]: 24,
  monthly: 12,
  quarterly: 4,
  yearly: 1,
};

type ProjectedCharges = { count: number; total: number; bounded: boolean };

type ProjectionWorkerInputs = {
  serviceDirection?: string;
  createdFromEnrollment?: boolean;
  timezone?: string;
  enrollmentBillingInterval?: string;
};

/** Return the service-period start used by the invoice worker for one due date. */
export function getProjectedServiceStart({
  dueDate,
  billingInterval,
  serviceDirection,
  createdFromEnrollment = false,
  timezone = 'utc',
}: {
  dueDate: number | string | Date;
  billingInterval?: string;
} & ProjectionWorkerInputs): number | null {
  const due = moment(dueDate).tz(timezone);
  if (!due.isValid()) return null;

  // Arrears cycles are governed by the worker's inclusive due-date cutoff and do
  // not use the advance service-start suppression.
  if (serviceDirection === SERVICE_DIRECTION.Previous) return null;

  if (!createdFromEnrollment || !serviceDirection) return due.clone().add(1, 'day').valueOf();
  if (!billingInterval) return null;

  if (isTwicePerMonth(billingInterval)) return due.clone().add(1, 'day').valueOf();
  if (billingInterval.toLowerCase() === 'monthly') return due.clone().startOf('month').add(1, 'month').valueOf();

  // This mirrors functions/getServicePeriod: enrollment-created non-monthly plans
  // are aligned to the next service week rather than simply dueDate + 1.
  const daysUntilSunday = (7 - due.day()) % 7;
  return due.clone().add(daysUntilSunday, 'day').add(1, 'day').valueOf();
}

/**
 * Project how much a recurring plan will charge:
 * - No `dateEnd`: the annualized figure — charges/year for the interval × per-charge amount.
 * - `dateEnd` set: the exact worker-eligible cycles beginning at `nextDueDate` when supplied
 *   (otherwise `dateStart`), walking the real cadence and advance-service cutoff.
 *
 * `total` is returned in the same unit as `perChargeAmount` (dollars). Returns null when the
 * interval has no known cadence.
 */
export function getProjectedCharges({
  perChargeAmount,
  billingInterval,
  dateStart,
  nextDueDate,
  dateEnd,
  serviceDirection,
  createdFromEnrollment = false,
  timezone = 'utc',
  enrollmentBillingInterval,
}: {
  perChargeAmount: number;
  billingInterval?: string;
  dateStart?: number | string | Date;
  nextDueDate?: number | string | Date;
  dateEnd?: number | string | Date;
} & ProjectionWorkerInputs): ProjectedCharges | null {
  const amount = perChargeAmount || 0;

  if (dateEnd == null) {
    const count = ANNUAL_CHARGE_COUNTS[billingInterval?.toLowerCase() ?? ''];
    if (count == null) return null;
    return { count, total: count * amount, bounded: false };
  }

  if (dateStart == null) return null;
  const momentStart = moment(dateStart).tz(timezone);
  const momentEnd = moment(dateEnd);
  if (!momentStart.isValid() || !momentEnd.isValid()) return null;

  const effectiveBillingInterval = billingInterval || (createdFromEnrollment ? enrollmentBillingInterval : undefined);

  const twicePerMonth = isTwicePerMonth(effectiveBillingInterval);
  const duration = twicePerMonth ? null : getBillingDuration(effectiveBillingInterval);
  if (!twicePerMonth && !duration) return null;

  const anchorDay = momentStart.date();
  let count = 0;
  let due = nextDueDate == null ? momentStart.clone() : moment(nextDueDate).tz(timezone);
  if (!due.isValid()) return null;
  const SAFETY_CAP = 10000;
  while (due.isSameOrBefore(momentEnd) && count < SAFETY_CAP) {
    const serviceStart = getProjectedServiceStart({
      dueDate: due.valueOf(),
      billingInterval: effectiveBillingInterval,
      serviceDirection,
      createdFromEnrollment,
      timezone,
    });
    if (
      serviceDirection !== SERVICE_DIRECTION.Previous &&
      serviceStart != null &&
      serviceStart >= momentEnd.valueOf()
    ) {
      break;
    }
    count++;
    due = twicePerMonth ? getNextTwicePerMonthDue(due, anchorDay) : due.clone().add(duration);
  }
  return { count, total: count * amount, bounded: true };
}

const parseWallClockDate = (date, timezone) => {
  const format = /^\d{4}-\d{2}-\d{2}/.test(date) ? 'YYYY-MM-DD' : DefaultDateFormat;
  return moment(date, format).tz(timezone, true);
};

/**
 * If the invoice plan is being created, set dates to start of day. If editing, keep the
 * time of day as is.
 */
const getMomentFromDate = (date, isEdit, timezone) => {
  const momentTime = isNaN(date) ? parseWallClockDate(date, timezone) : moment(date).tz(timezone);
  return isEdit ? momentTime : momentTime.startOf('day');
};

export const getDateEnd = (date, timezone) => {
  if (!date) return null;
  if (!isNaN(date)) return date;

  const momentEnd = parseWallClockDate(date, timezone).endOf('day');
  return momentEnd.utc().valueOf();
};

export function getUpdatedBillingDates({
  invoicePlan,
  timezone,
  isEditing,
}: {
  invoicePlan: any;
  timezone?: string;
  isEditing?: boolean;
}) {
  const isEdit = isEditing || false;

  timezone = timezone || 'utc';

  const momentNow = moment().tz(timezone).startOf('day');
  const momentSendPrevious = getMomentFromDate(invoicePlan.dateSend, isEdit, timezone);

  const isSendDateInThePast = momentSendPrevious.isBefore(momentNow);

  const momentStart = getMomentFromDate(invoicePlan.dateStart, isEdit, timezone);
  let momentDue = momentStart.clone();

  if (isSendDateInThePast) {
    // The plan's billing anchor is its due-date day-of-month (e.g. the 17th), which can
    // differ from the start-date day (e.g. the 1st). Catch up from the existing due date
    // so a lapsed or paused/resumed plan keeps its billing day instead of snapping to the
    // start-date day (PAYM-5006). Plans that have never been billed (no dateDue yet) fall
    // back to the start date, preserving the original behavior.
    const momentAnchor = invoicePlan.dateDue
      ? getMomentFromDate(invoicePlan.dateDue, isEdit, timezone)
      : momentStart.clone();
    momentDue = momentAnchor.clone();

    if (isTwicePerMonth(invoicePlan.billingInterval)) {
      // Semi-monthly cadence is anchored to the start-date day-of-month rather than
      // advanced by a fixed duration. Anchoring to the due day instead would break the
      // {16, 31} pair: a plan whose last due landed on the 16th would recompute to
      // {1, 16}, shifting the month-end charge ~2 weeks early and dropping it.
      const anchorDay = momentStart.date();
      while (momentDue.isBefore(momentNow)) {
        momentDue = getNextTwicePerMonthDue(momentDue, anchorDay);
      }
    } else {
      while (momentDue.isBefore(momentNow)) {
        momentDue.add(getBillingDuration(invoicePlan.billingInterval));
      }
    }
  } else if (isEdit && invoicePlan.dateDue) {
    momentDue = getMomentFromDate(invoicePlan.dateDue, isEdit, timezone);
  }

  const convertedNotificationDays =
    typeof invoicePlan.notificationDays === 'string'
      ? parseInt(invoicePlan.notificationDays, 10)
      : invoicePlan.notificationDays;
  const momentSend = momentDue.clone().subtract(convertedNotificationDays, 'days');

  return {
    dateStart: momentStart.utc().valueOf(),
    dateEnd: getDateEnd(invoicePlan.dateEnd, timezone),
    dateDue: momentDue.utc().valueOf(),
    dateSend: momentSend.utc().valueOf(),
  };
}

const formatServiceDate = (momentDate) => momentDate.format(DefaultDateFormat);

export const getBillingServiceInterval = (dateStart, billingInterval, direction, timezone = 'utc', isEdit = true) => {
  const momentStart = getMomentFromDate(dateStart, isEdit, timezone);

  if (isTwicePerMonth(billingInterval)) {
    // Semi-monthly: the service window is the half-month between consecutive due dates.
    const anchorDay = momentStart.date();
    const momentServiceStart =
      direction === SERVICE_DIRECTION.Previous
        ? getPreviousTwicePerMonthDue(momentStart, anchorDay).add(1, 'days')
        : momentStart.clone().add(1, 'days');
    const momentServiceEnd =
      direction === SERVICE_DIRECTION.Previous ? momentStart.clone() : getNextTwicePerMonthDue(momentStart, anchorDay);

    return formatServiceDate(momentServiceStart) + ' - ' + formatServiceDate(momentServiceEnd);
  }

  const momentBillingInterval = getBillingDuration(billingInterval);
  const momentServiceStart =
    direction === SERVICE_DIRECTION.Previous
      ? momentStart.clone().subtract(momentBillingInterval).add(1, 'days')
      : momentStart.clone().add(1, 'days');
  const momentServiceEnd =
    direction === SERVICE_DIRECTION.Previous ? momentStart.clone() : momentStart.clone().add(momentBillingInterval);

  return formatServiceDate(momentServiceStart) + ' - ' + formatServiceDate(momentServiceEnd);
};

type IntervalTransitionServicePeriodInput = {
  dateServiceEnd: number;
  dateDue: number;
  dateStart: number;
  billingInterval: string;
  timezone: string;
};

export function getIntervalTransitionServicePeriod({
  dateServiceEnd,
  dateDue,
  dateStart,
  billingInterval,
  timezone,
}: IntervalTransitionServicePeriodInput) {
  const serviceStart = moment(dateServiceEnd).tz(timezone).startOf('day').add(1, 'day');
  const due = moment(dateDue).tz(timezone);
  let serviceEnd;

  if (isTwicePerMonth(billingInterval)) {
    const anchorDay = moment(dateStart).tz(timezone).date();
    const nextDue = getNextTwicePerMonthDue(due, anchorDay);
    serviceEnd = serviceStart.clone().add(nextDue.diff(due, 'days'), 'days').subtract(1, 'day');
  } else {
    const duration = getBillingDuration(billingInterval);
    if (!duration) throw new Error('Invalid billing interval.');
    serviceEnd = serviceStart.clone().add(duration).subtract(1, 'day');
  }

  return {
    dateServiceStart: Number(serviceStart.format('x')),
    dateServiceEnd: Number(serviceEnd.format('x')),
    displayInterval: `${formatServiceDate(serviceStart)} - ${formatServiceDate(serviceEnd)}`,
  };
}

// Billing runs in the primary location's zone; no primary in the list means no label.
export const getPrimaryLocationTimezone = (locations?: Array<{ primary?: boolean; timezone?: string }> | null) =>
  locations?.find((location) => location.primary)?.timezone;

type NextChargePlan = {
  dateDue?: number;
  dateStart?: number;
  dateEnd?: number;
  dateServiceEnd?: number;
  billingInterval?: string;
  serviceDirection?: string | null;
  isServiceDatesEnabled?: boolean;
  createdFromEnrollment?: boolean;
  servicePeriodCalculationMode?: string;
  students?: Array<{ enrollment?: { tuitionAndFees?: { tuition?: { billingInterval?: string } } } }>;
};

/**
 * Service period the plan's next charge covers, formatted like the plan form's preview.
 * Mirrors functions/getServicePeriod (no shared package); keep the two in sync.
 */
export function getNextChargeServicePeriod(plan: NextChargePlan | null, timezone?: string | null): string | null {
  if (!timezone || !moment.tz.zone(timezone)) return null;
  if (!plan?.dateDue || !plan.serviceDirection || plan.isServiceDatesEnabled === false) return null;
  const direction = plan.serviceDirection;
  const interval =
    plan.billingInterval ||
    (plan.createdFromEnrollment ? plan.students?.[0]?.enrollment?.tuitionAndFees?.tuition?.billingInterval : undefined);
  if (!interval) return null;

  // Worker ends the plan instead of charging when the due date or an advance service start reaches dateEnd.
  const planEnd = plan.dateEnd ? moment(plan.dateEnd) : null;
  if (planEnd && moment(plan.dateDue).isAfter(planEnd)) return null;
  const endsBeforeCharge = (serviceStart: moment.Moment) =>
    !!planEnd && direction !== SERVICE_DIRECTION.Previous && serviceStart.isSameOrAfter(planEnd);

  if (plan.servicePeriodCalculationMode === 'due-to-due') {
    if (!plan.dateServiceEnd || !plan.dateStart) return null;
    const period = getIntervalTransitionServicePeriod({
      dateServiceEnd: plan.dateServiceEnd,
      dateDue: plan.dateDue,
      dateStart: plan.dateStart,
      billingInterval: interval,
      timezone,
    });
    return endsBeforeCharge(moment(period.dateServiceStart)) ? null : period.displayInterval;
  }

  const due = moment(plan.dateDue).tz(timezone);
  let start;
  let end;
  if (isTwicePerMonth(interval)) {
    const anchorDay = moment(plan.dateStart).tz(timezone).date();
    if (direction === SERVICE_DIRECTION.Previous) {
      start = getPreviousTwicePerMonthDue(due, anchorDay).add(1, 'day');
      end = due.clone();
    } else {
      start = due.clone().add(1, 'day');
      end = getNextTwicePerMonthDue(due, anchorDay);
    }
  } else if (!plan.createdFromEnrollment) {
    if (endsBeforeCharge(due.clone().add(1, 'day'))) return null;
    return getBillingServiceInterval(plan.dateDue, interval, direction, timezone, true);
  } else if (interval.toLowerCase() === 'monthly') {
    start = due.clone().startOf('month');
    if (direction !== SERVICE_DIRECTION.Previous) start.add(1, 'month');
    end = start.clone().endOf('month');
  } else {
    const week = due.clone().add((7 - due.day()) % 7, 'day');
    if (direction === SERVICE_DIRECTION.Previous) week.subtract(1, 'week');
    start = week.clone().add(1, 'day');
    end = week.clone().add(7, 'day');
  }
  if (endsBeforeCharge(start)) return null;
  return `${formatServiceDate(start)} - ${formatServiceDate(end)}`;
}
